/* Release 6.10 (spec 29, section 4). The Lojas parceiras page script.
   - WhatsApp: the number and link come from the footer's own WhatsApp link
     (footer-editorial-tokens, setting whatsapp_number), never typed here. The
     buttons stay hidden when the footer has none. The prefilled text is the
     page's data-wa-text (au_r610.form.whatsapp_text).
   - Tabs (Para cada tipo de negócio): tab roles, roving tabindex, arrow keys,
     Home and End.
   - The form keeps today's submission: before it posts, the lead score, tier,
     reasons, internal tags, the company mirror and the email summary are set
     the way av-b2b-partner-landing sets them, with the summary labels of
     today's form. The volume points come from the option's data attributes.
   - Phone bar: hidden while the form is in view.
   - Events (allow list in av-analytics-v53.js): b2b_whatsapp_click,
     b2b_portal_click and b2b_form_submit, with placement. */
(() => {
  const root = document.querySelector('[data-au-r610]');
  if (!root) return;
  const track = (name, payload) => { try { window.AVAnalytics?.custom?.(name, payload); } catch (_) { /* analytics are optional */ } };

  /* WhatsApp from the footer */
  const footerLink = [...document.querySelectorAll('a[href*="wa.me/"]')].find((a) => !root.contains(a));
  const digits = footerLink ? (footerLink.getAttribute('href').match(/wa\.me\/(\d{10,13})/) || [])[1] : '';
  if (digits) {
    const shown = (footerLink.textContent.match(/\(\d{2}\)\s?\d{4,5}[\s-]?\d{4}/) || [])[0] || '';
    const href = `https://wa.me/${digits}?text=${encodeURIComponent(root.dataset.waText || '')}`;
    root.querySelectorAll('[data-au-r610-wa]').forEach((a) => { a.href = href; a.hidden = false; });
    if (shown) {
      root.querySelectorAll('[data-au-r610-wa-number]').forEach((n) => { n.textContent = shown.replace(/ /g, ' '); });
      root.querySelectorAll('[data-au-r610-wa-line]').forEach((n) => { n.hidden = false; });
    }
  }
  root.addEventListener('click', (event) => {
    const wa = event.target.closest('[data-au-r610-wa]');
    if (wa) track('b2b_whatsapp_click', { placement: wa.dataset.auR610Wa });
    const portal = event.target.closest('[data-au-r610-portal]');
    if (portal) track('b2b_portal_click', { placement: portal.dataset.auR610Portal });
  });

  /* Tabs */
  const tablist = root.querySelector('[data-au-r610-tabs]');
  if (tablist) {
    const tabs = [...tablist.querySelectorAll('[role="tab"]')];
    const select = (tab, focus) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      if (focus) tab.focus();
    };
    tablist.addEventListener('click', (event) => { const tab = event.target.closest('[role="tab"]'); if (tab) select(tab, false); });
    tablist.addEventListener('keydown', (event) => {
      const i = tabs.indexOf(document.activeElement);
      if (i < 0) return;
      let next = null;
      if (event.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
      else if (event.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
      else if (event.key === 'Home') next = tabs[0];
      else if (event.key === 'End') next = tabs[tabs.length - 1];
      if (next) { event.preventDefault(); select(next, true); }
    });
  }

  /* Form: today's lead score and summary */
  const form = root.querySelector('[data-au-r610-form]');
  if (form) {
    const q = (sel) => form.querySelector(sel);
    const cnpj = q('[data-b2b-cnpj]');
    if (cnpj) cnpj.addEventListener('input', () => {
      const d = cnpj.value.replace(/\D/g, '').slice(0, 14);
      let out = d.slice(0, 2);
      if (d.length > 2) out += '.' + d.slice(2, 5);
      if (d.length > 5) out += '.' + d.slice(5, 8);
      if (d.length > 8) out += '/' + d.slice(8, 12);
      if (d.length > 12) out += '-' + d.slice(12, 14);
      cnpj.value = out;
    });
    /* Today's soft check: a CNPJ that fails its check digits shows the
       warning, and the form can still be sent (strict validation is off). */
    const validCnpj = (value) => {
      const c = value.replace(/\D/g, '');
      if (c.length !== 14 || /^(\d)\1+$/.test(c)) return false;
      const digit = (len) => {
        let sum = 0;
        let pos = len - 7;
        for (let i = len; i >= 1; i--) {
          sum += Number(c.charAt(len - i)) * pos--;
          if (pos < 2) pos = 9;
        }
        return sum % 11 < 2 ? 0 : 11 - (sum % 11);
      };
      return digit(12) === Number(c.charAt(12)) && digit(13) === Number(c.charAt(13));
    };
    const cnpjWarning = q('[data-b2b-cnpj-warning]');
    const cnpjState = () => {
      const show = Boolean(cnpj.value) && !validCnpj(cnpj.value);
      cnpjWarning.hidden = !show;
      if (show) cnpj.setAttribute('aria-describedby', cnpjWarning.id); else cnpj.removeAttribute('aria-describedby');
    };
    if (cnpj && cnpjWarning) {
      cnpj.addEventListener('blur', cnpjState);
      cnpj.addEventListener('input', () => { if (!cnpjWarning.hidden) cnpjState(); });
    }
    const prepare = () => {
      const volume = q('[data-b2b-volume]');
      const option = volume && volume.selectedOptions[0];
      const score = option && option.value ? Number(option.dataset.score) || 0 : 0;
      const reasons = option && option.value && option.dataset.reason ? [option.dataset.reason] : [];
      let tier = 'C lead';
      if (score >= 75) tier = 'A lead';
      else if (score >= 45) tier = 'B lead';
      const set = (sel, value) => { const n = q(sel); if (n) n.value = value; };
      set('[data-b2b-lead-tier]', tier);
      set('[data-b2b-lead-score]', String(score));
      set('[data-b2b-lead-reason]', reasons.join('; ') || 'Cadastro inicial sem sinais suficientes');
      const tags = q('[data-b2b-tags]');
      if (tags) tags.value = (tags.dataset.b2bTagsBase || '') + ',' + tier.toLowerCase().replace(/\s+/g, '-');
      set('[data-b2b-company-mirror]', (q('[data-b2b-company-source]') || {}).value || '');
      const lines = [];
      form.querySelectorAll('[data-summary]').forEach((control) => {
        const value = control.tagName === 'SELECT' ? (control.value ? control.selectedOptions[0].textContent.trim() : '') : control.value.trim();
        if (value) lines.push(`${control.dataset.summary}: ${value}`);
      });
      lines.push(`Lead score: ${score}`, `Lead tier: ${tier}`, `Motivos: ${reasons.join('; ') || 'Cadastro inicial sem sinais suficientes'}`);
      set('[data-b2b-email-summary]', lines.join(' | '));
    };
    form.addEventListener('change', prepare);
    form.addEventListener('submit', () => { prepare(); if (form.checkValidity()) track('b2b_form_submit', { placement: 'form' }); });
    prepare();
  }

  /* Phone bar: hidden while the form is in view */
  const bar = root.querySelector('[data-au-r610-bar]');
  const area = root.querySelector('[data-au-r610-form-area]');
  if (bar && area && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => { bar.classList.toggle('is-hidden', entry.isIntersecting); }).observe(area);
  }
})();
